import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { cancelHire, completeHiredApplication, fetchUserCv, rateJob } from '../../../services/api';
import { getMediaUrl } from '../../../utils/mediaUrl';
import { getCallLink } from '../../../utils/phoneLinks';
import { CvModal } from './ClientApplicationJobCard';
import CardIcon from '../../../shared/components/icons/CardIcon';

function formatDate(date) {
  if (!date) return 'Date unavailable';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ContactModal({ worker, job, onClose }) {
  const phone = worker.phone || worker.whatsappNumber;
  const whatsapp = worker.whatsappNumber || worker.phone;
  const message = encodeURIComponent(`Hello ${worker.firstName || 'there'}, I am contacting you about the ${job.title || 'job'} job on Tuma Jobs. Please confirm your availability and the planned start time.`);
  const whatsappLink = whatsapp ? `https://wa.me/${whatsapp.replace(/\D/g, '').replace(/^0(\d)/, '260$1')}?text=${message}` : '';

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <section className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="contact-worker-title">
        <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-bold uppercase tracking-widest text-orange-600">Contact worker</p><h2 id="contact-worker-title" className="mt-1 text-xl font-bold text-navy">{worker.fullName || `${worker.firstName || ''} ${worker.lastName || ''}`.trim()}</h2></div><button type="button" onClick={onClose} aria-label="Close contact options" className="text-2xl leading-none text-slate-400 hover:text-slate-900">&times;</button></div>
        <p className="mt-3 text-sm leading-6 text-slate-600">Choose how you would like to discuss the {job.title || 'job'} assignment.</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {phone && <a href={getCallLink(phone)} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-navy px-4 py-3 text-sm font-semibold text-white hover:opacity-90"><CardIcon name="phone" />Call</a>}
          {phone && <a href={`sms:${phone}`} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50"><CardIcon name="message" />Text</a>}
          {whatsappLink && <a href={whatsappLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700"><CardIcon name="message" />WhatsApp</a>}
        </div>
      </section>
    </div>
  );
}

function HiredJobCard({ group, onCompleted }) {
  const [cv, setCv] = useState(null);
  const [contactWorker, setContactWorker] = useState(null);
  const [cancelHireTarget, setCancelHireTarget] = useState(null);
  const [ratingTarget, setRatingTarget] = useState(null);
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const job = group.job || {};
  const hiredWorkers = group.hiredApplicants || [];

  const openCv = async (worker) => {
    setLoading(true);
    try {
      const data = await fetchUserCv(worker.id);
      setCv({ data, worker });
    } catch (error) {
      toast.error(error.message || 'Unable to load CV.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelHire = async (entry) => {
    if (!entry?.applicationId) {
      toast.error('Unable to identify this hire.');
      return;
    }

    setLoading(true);
    try {
      await cancelHire(entry.applicationId);
      toast.success('Hire cancelled successfully.');
      setCancelHireTarget(null);
      await onCompleted?.();
    } catch (error) {
      toast.error(error.message || 'Unable to cancel hire.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteWork = async (entry) => {
    if (!entry?.applicationId) {
      toast.error('Unable to identify this hire.');
      return;
    }

    setLoading(true);
    try {
      await completeHiredApplication(entry.applicationId);
      setRatingTarget(entry);
      setRatingScore(0);
      setRatingComment('');
    } catch (error) {
      toast.error(error.message || 'Unable to complete this worker\'s work.');
    } finally {
      setLoading(false);
    }
  };

  const openRating = (entry) => {
    setRatingTarget(entry);
    setRatingScore(entry.worker?.rating?.score || 0);
    setRatingComment(entry.worker?.rating?.comment || '');
  };

  const handleRateWorker = async () => {
    const workerId = ratingTarget?.worker?.id;
    if (!ratingTarget?.applicationId || !workerId || !ratingScore) return;

    setLoading(true);
    try {
      await rateJob(job.id, {
        toUserId: workerId,
        score: ratingScore,
        comment: ratingComment.trim(),
      });
      setRatingTarget(null);
      setSuccessMessage('The worker\'s work was completed and the rating was saved successfully.');
    } catch (error) {
      toast.error(error.message || 'Unable to save worker rating.');
    } finally {
      setLoading(false);
    }
  };

  const closeSuccessModal = async () => {
    setSuccessMessage('');
    await onCompleted?.();
  };

  const jobStatus = hiredWorkers[0]?.hire?.status || job.status || 'ACTIVE';
  const jobStatusLabel = String(jobStatus).toUpperCase();
  const jobStatusClass = jobStatusLabel === 'COMPLETED'
    ? 'border-sky-200 bg-sky-50 text-sky-700'
    : jobStatusLabel === 'ACTIVE'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-slate-200 bg-slate-50 text-slate-700';
  const budget = job.budget ?? hiredWorkers[0]?.hire?.agreedAmount ?? 0;
  const hiredDate = hiredWorkers.reduce((earliest, entry) => {
    const date = entry.hire?.hiredAt || entry.appliedAt;
    return !earliest || (date && new Date(date) < new Date(earliest)) ? date : earliest;
  }, null);

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div><p className="text-xs font-bold uppercase tracking-widest text-orange-600">{job.position || job.title || 'Hired job'}</p><h2 className="mt-2 text-2xl font-bold text-navy">{job.title || 'Untitled job'}</h2></div>
        <span className={`inline-flex w-fit shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold ${jobStatusClass}`}><CardIcon name={jobStatusLabel === 'COMPLETED' ? 'check' : 'briefcase'} />{jobStatusLabel}</span>
      </div>
      <div className="mt-6 grid gap-3 border-b border-slate-200 pb-6 text-sm text-slate-700 sm:grid-cols-2">
        <p className="inline-flex items-center gap-1.5"><CardIcon name="location" /><strong>{job.location || [job.area, job.city].filter(Boolean).join(', ') || 'Location unavailable'}</strong></p>
        <p className="inline-flex items-center gap-1.5"><CardIcon name="money" /><strong>K{budget} per worker</strong>{job.isNegotiable && ' · Negotiable'}</p>
        <p className="inline-flex items-center gap-1.5"><CardIcon name="people" /><strong>{hiredWorkers.length} / {job.requiredPeople || hiredWorkers.length} workers hired</strong></p>
        <p className="inline-flex items-center gap-1.5"><CardIcon name="clock" /><strong>{(job.jobType || 'PART_TIME').replace('_', ' ')}</strong> · <CardIcon name="flame" /><strong>{String(job.urgency || 'Normal').toUpperCase()}</strong></p>
        <p className="inline-flex items-center gap-1.5"><CardIcon name="calendar" />Hired <strong>{formatDate(hiredDate || job.updatedAt)}</strong></p>
      </div>
      <div className="border-b border-slate-200 py-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-navy">Hired workers</h3>
        <div className="mt-4 space-y-4">
          {hiredWorkers.map((entry) => {
            const worker = entry.worker || {};
            const workerName = worker.fullName || `${worker.firstName || ''} ${worker.middleName || ''} ${worker.lastName || ''}`.trim() || 'Hired worker';
            const photo = getMediaUrl(worker.profilePhoto || worker.profile?.profilePhoto);
            const initials = `${worker.firstName?.[0] || ''}${worker.lastName?.[0] || ''}`.toUpperCase() || 'W';
            const rating = Number(worker.rating || 0);
            const hireStatus = String(entry.hire?.status || entry.status || 'ACTIVE').toUpperCase();
            return (
              <div key={entry.applicationId} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full bg-orange-100 font-bold text-orange-700">{photo ? <img src={photo} alt={workerName} className="h-full w-full object-cover" /> : initials}</div>
                    <div>
                      <h4 className="font-bold text-navy">{workerName}</h4>
                      <p className="inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="star" className="h-3.5 w-3.5 text-orange-500" />{rating ? `${rating} · ` : 'New worker · '}{worker.completedJobs || 0} completed jobs</p>
                      <p className="inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="location" />{[worker.area, worker.city].filter(Boolean).join(', ') || 'Location unavailable'}</p>
                    </div>
                  </div>
                  <span className={`inline-flex w-fit shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold ${hireStatus === 'COMPLETED' ? 'border-sky-200 bg-sky-50 text-sky-700' : hireStatus === 'ACTIVE' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-700'}`}><CardIcon name={hireStatus === 'COMPLETED' ? 'check' : 'briefcase'} />{hireStatus}</span>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  <button type="button" onClick={() => openCv(worker)} disabled={loading} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"><CardIcon name="file" />{loading ? 'Loading...' : 'View CV'}</button>
                  <button type="button" onClick={() => setContactWorker(worker)} disabled={loading} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-navy px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"><CardIcon name="message" />Contact</button>
                  {hireStatus === 'ACTIVE' && <button type="button" onClick={() => handleCompleteWork(entry)} disabled={loading} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"><CardIcon name="check" />{loading ? 'Updating...' : 'Complete work'}</button>}
                  {hireStatus === 'COMPLETED' && <button type="button" onClick={() => openRating(entry)} disabled={loading} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"><CardIcon name="star" />Rate Worker</button>}
                  <button type="button" onClick={() => setCancelHireTarget(entry)} disabled={loading || hireStatus !== 'ACTIVE'} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"><CardIcon name="x" />{loading ? 'Updating...' : 'Cancel hire'}</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {cv && <CvModal cv={cv.data} applicant={{ applicant: cv.worker }} job={job} onClose={() => setCv(null)} hideShare />}
      {contactWorker && <ContactModal worker={contactWorker} job={job} onClose={() => setContactWorker(null)} />}
      {ratingTarget && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <section className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="rate-hired-worker-title">
            <h2 id="rate-hired-worker-title" className="text-xl font-bold text-navy">Rate {ratingTarget.worker?.firstName || 'worker'}</h2>
            <div className="mt-5 flex gap-2">{[1, 2, 3, 4, 5].map((value) => <button key={value} type="button" onClick={() => setRatingScore(value)} aria-label={`${value} stars`} className={`${value <= ratingScore ? 'text-orange-500' : 'text-slate-300'}`}><CardIcon name="starFilled" className="h-8 w-8" /></button>)}</div>
            <textarea value={ratingComment} onChange={(event) => setRatingComment(event.target.value)} rows="3" maxLength="500" placeholder="Add a comment (optional)" className="mt-5 w-full rounded-2xl border border-slate-200 p-3 text-sm" />
            <div className="mt-5 flex justify-end gap-3"><button type="button" onClick={() => { setRatingTarget(null); setSuccessMessage('The worker\'s work was completed successfully.'); }} disabled={loading} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">Skip rating</button><button type="button" onClick={handleRateWorker} disabled={!ratingScore || loading} className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{loading ? 'Saving...' : 'Save rating'}</button></div>
          </section>
        </div>
      )}
      {successMessage && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <section className="w-full max-w-md rounded-3xl bg-white p-6 text-center shadow-2xl" role="alertdialog" aria-modal="true" aria-labelledby="hired-success-title">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-emerald-700"><CardIcon name="check" className="h-7 w-7" /></div>
            <h2 id="hired-success-title" className="mt-4 text-xl font-bold text-navy">Success</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">{successMessage}</p>
            <button type="button" onClick={closeSuccessModal} className="mt-6 rounded-xl bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Done</button>
          </section>
        </div>
      )}
      {cancelHireTarget && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4">
          <section className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" role="dialog" aria-modal="true">
            <h2 className="text-xl font-bold text-navy">Cancel this hire?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">This will cancel the hire for <strong>{cancelHireTarget.worker?.fullName || 'this worker'}</strong>. This action cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setCancelHireTarget(null)} disabled={loading} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700"><CardIcon name="x" />Cancel</button>
              <button type="button" onClick={() => handleCancelHire(cancelHireTarget)} disabled={loading} className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white"><CardIcon name="x" />{loading ? 'Updating...' : 'Confirm cancel'}</button>
            </div>
          </section>
        </div>
      )}
    </article>
  );
}

export default HiredJobCard;
